import { BackgroundPattern, PATTERN_GROUPS, PATTERNS, patternTile } from "@keepoinc/site-page-react-component";
import { CSSProperties, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { SAVE_BAR_Z_INDEX } from "../../../../../../components/SaveBar/styles";
import { classNames } from "../../../../../../utils/styles";
import { fieldLabel } from "../../styles";

const prefix = "pages.editor.settings.groups.backgroundFields.pattern";

const NAMES = Object.keys(PATTERNS) as BackgroundPattern[];

/* The field and its panel of samples: also the top photo's frames (Topo › Formato) */
export const PickerContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-family: Inter, sans-serif;

  .role {
    ${fieldLabel}
  }

  /* as a font's field: the drawing chosen and its name */
  .value {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    width: 100%;
    height: 52px;
    padding: 0 1rem 0 0.5rem;
    color: var(--color-neutral-white);
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    background: var(--color-neutral-base);
    border: 2px solid transparent;
    border-radius: 12px;

    &[aria-expanded="true"] {
      border-color: var(--color-brand-blue);
    }

    /* the arrow of a list */
    &::after {
      flex-shrink: 0;
      width: 8px;
      height: 8px;
      margin-left: auto;
      content: "";
      border-right: 2px solid currentColor;
      border-bottom: 2px solid currentColor;
      transform: translateY(-2px) rotate(45deg);
    }
  }

  .swatch {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 8px;
  }

  /* over the fixed Salvar bar, as the fonts' list */
  .panel {
    position: absolute;
    top: calc(100% + 0.25rem);
    left: 0;
    z-index: ${SAVE_BAR_Z_INDEX + 1};
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    max-height: min(26rem, 60vh);
    padding: 1rem;
    overflow: auto;
    background: var(--color-neutral-darkness);
    border: 1px solid var(--color-neutral-base);
    border-radius: 12px;
  }

  .group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .drawings {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(52px, 1fr));
    gap: 0.5rem;

    button {
      aspect-ratio: 1;
      border: 2px solid transparent;
      border-radius: 10px;

      &:hover {
        border-color: rgba(255, 255, 255, 0.35);
      }

      &.selected {
        border-color: var(--color-brand-blue);
      }
    }
  }
`;

type Props = {
  value: BackgroundPattern;
  /** The drawing's color. */
  color: string;
  /** The page's color, under the drawing (as on the Keepo). */
  pageColor: string;
  /** How strong the drawing is (%): the samples show it so. */
  opacity?: number;
  onPick: (pattern: BackgroundPattern) => void;
};

/** "Desenho": the pattern chosen, opening the drawings by group, each shown in the Keepo's colors. */
export const PatternPicker = ({ value, color, pageColor, opacity, onPick }: Props) => {
  const { t } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLButtonElement>(null);
  const [isOpen, setOpen] = useState(false);

  const swatch = (pattern: BackgroundPattern): CSSProperties => ({
    backgroundColor: pageColor,
    ...patternTile(pattern, color, opacity),
  });

  // closes on a click outside it
  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);

  // opened: the drawing chosen takes the focus (and is in sight)
  useEffect(() => {
    if (isOpen) container.current?.querySelector<HTMLButtonElement>('.drawings [aria-checked="true"]')?.focus();
  }, [isOpen]);

  const close = () => {
    setOpen(false);
    field.current?.focus();
  };

  const keyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      close();
    }
  };

  const label = t(`${prefix}.drawing`);
  const name = (pattern: BackgroundPattern) => t(`${prefix}.drawings.${pattern}`);

  return (
    <PickerContainer ref={container} onKeyDown={keyDown}>
      <span className="role" aria-hidden="true">
        {label}
      </span>
      <button
        ref={field}
        type="button"
        className="value"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`${label}: ${name(value)}`}
        onClick={() => setOpen(!isOpen)}>
        <span className="swatch" style={swatch(value)} />
        {name(value)}
      </button>
      {isOpen && (
        <div className="panel" role="group" aria-label={label}>
          {PATTERN_GROUPS.map(group => (
            <div className="group" key={group}>
              <span className="role">{t(`${prefix}.groups.${group}`)}</span>
              <div className="drawings" role="radiogroup" aria-label={t(`${prefix}.groups.${group}`)}>
                {NAMES.filter(pattern => PATTERNS[pattern].group === group).map(pattern => (
                  <button
                    key={pattern}
                    type="button"
                    role="radio"
                    aria-checked={pattern === value}
                    aria-label={name(pattern)}
                    title={name(pattern)}
                    className={classNames({ selected: pattern === value })}
                    style={swatch(pattern)}
                    onClick={() => {
                      onPick(pattern);
                      close();
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </PickerContainer>
  );
};
